// src/context/CaseDataContext.js
// Centralized case data context - ek hi API call, saara data distribute

import React, { createContext, useContext, useState, useCallback, useRef, useEffect } from 'react';
import axios from 'axios';

const CaseDataContext = createContext(null);

const API_BASE = 'https://hms.backend.hiragroupcloud.com/api';

// ============================================
// Helper: Get user data from localStorage
// ============================================
export const getUserData = () => {
  try {
    const possibleKeys = ['user', 'userData', 'auth', 'currentUser', 'labUser', 'labUserData'];

    for (const key of possibleKeys) {
      const storedData = localStorage.getItem(key);
      if (storedData) {
        try {
          const parsed = JSON.parse(storedData);
          const userRole =
            parsed.userRole || parsed.role || parsed.data?.userRole || parsed.data?.role ||
            parsed.user?.userRole || parsed.user?.role;
          const userId =
            parsed.userId || parsed.id || parsed.data?.userId || parsed.data?.id ||
            parsed.user?.userId || parsed.user?.id;

          if (userRole || userId) {
            return { userRole, userId };
          }
        } catch (e) {
          continue;
        }
      }
    }
    return { userRole: null, userId: null };
  } catch (error) {
    return { userRole: null, userId: null };
  }
};

// ============================================
// Helper: Get userLabId from localStorage
// ============================================
export const getUserLabId = () => {
  try {
    const possibleKeys = ['user', 'userData', 'auth', 'currentUser', 'labUser', 'labUserData'];

    for (const key of possibleKeys) {
      const storedData = localStorage.getItem(key);
      if (storedData) {
        try {
          const parsed = JSON.parse(storedData);
          const userLabId =
            parsed.userLabId || parsed.labId || parsed.id ||
            parsed.data?.userLabId || parsed.data?.labId ||
            parsed.user?.userLabId || parsed.user?.labId;

          if (userLabId) return userLabId;

          if (typeof storedData === 'string' && storedData.match(/^000000\d{2}$/)) {
            return storedData;
          }
        } catch (e) {
          continue;
        }
      }
    }

    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      const value = localStorage.getItem(key);
      if (value && value.match(/^000000\d{2}$/)) {
        return value;
      }
    }
    return null;
  } catch (error) {
    return null;
  }
};

// ============================================
// Provider Component
// ============================================
export const CaseDataProvider = ({ caseId, children }) => {
  const [caseData, setCaseData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Extra data (reference, remake, outsource, account)
  const [referenceCaseData, setReferenceCaseData] = useState(null);
  const [remakeCases, setRemakeCases] = useState([]);
  const [outsourceCaseData, setOutsourceCaseData] = useState(null);
  const [accountDetails, setAccountDetails] = useState(null);

  // Refs to prevent duplicate fetches
  const mainFetched = useRef(false);
  const referenceFetched = useRef(false);
  const remakeFetched = useRef(false);
  const outsourceFetched = useRef(false);
  const accountFetched = useRef(false);

  // ============================================
  // MAIN CASE FETCH - ek hi baar, saara data
  // ============================================
  const fetchCaseData = useCallback(async (force = false) => {
    if (!caseId) return;
    if (mainFetched.current && !force) return;

    try {
      const response = await axios.get(`${API_BASE}/cases/cases/${caseId}`);
      setCaseData(response.data);
      mainFetched.current = true;

      // Trigger dependent fetches in parallel (not sequential)
      const data = response.data;

      // Reference case
      if (data?.remakeReference?.previousCaseNumber && !referenceFetched.current) {
        referenceFetched.current = true;
        axios.get(`${API_BASE}/cases/cases/${data.remakeReference.previousCaseNumber}`)
          .then(res => setReferenceCaseData(res.data))
          .catch(err => console.error('Reference case fetch error:', err));
      }

      // Remake cases
      if (data?.caseNumber && !remakeFetched.current) {
        remakeFetched.current = true;
        axios.get(`${API_BASE}/cases/cases`)
          .then(res => {
            let allCases = [];
            if (Array.isArray(res.data)) allCases = res.data;
            else if (res.data?.data && Array.isArray(res.data.data)) allCases = res.data.data;
            else if (res.data?.cases && Array.isArray(res.data.cases)) allCases = res.data.cases;

            const remakeData = allCases.filter(
              c => c.isRemake === true && c.remakeReference?.previousCaseNumber === data.caseNumber
            );
            setRemakeCases(remakeData);
          })
          .catch(err => console.error('Remake cases fetch error:', err));
      }

      // Outsource case
      if (data?.outsourcedTo?.length > 0 && !outsourceFetched.current) {
        outsourceFetched.current = true;
        const userLabId = getUserLabId();
        if (userLabId) {
          axios.get(`${API_BASE}/outsource-case/case/${data.caseNumber}`, {
            headers: { 'userLabId': userLabId, 'x-user-lab-id': userLabId }
          })
            .then(res => {
              if (res.data?.success || res.data?.data) {
                setOutsourceCaseData(res.data?.data || res.data);
              }
            })
            .catch(() => {
              // Fallback
              axios.get(`${API_BASE}/outsource-case/by-case-number/${data.caseNumber}`)
                .then(res => {
                  if (res.data?.success || res.data?.data) {
                    setOutsourceCaseData(res.data?.data || res.data);
                  }
                })
                .catch(err => console.error('Outsource fallback error:', err));
            });
        }
      }

      // Account details
      if (data?.account_holder && !accountFetched.current) {
        accountFetched.current = true;
        const accountId = data.account_holder;
        if (typeof accountId === 'string' && !isNaN(accountId)) {
          axios.get(`${API_BASE}/account/account/${accountId}`)
            .then(res => setAccountDetails(res.data))
            .catch(err => console.error('Account fetch error:', err));
        } else {
          // Try to find by name
          axios.get(`${API_BASE}/account/account`)
            .then(res => {
              const accounts = res.data?.data || res.data || [];
              const found = accounts.find(a =>
                a.accountId === accountId ||
                a.account_name?.toLowerCase() === String(accountId).toLowerCase()
              );
              if (found) {
                return axios.get(`${API_BASE}/account/account/${found.accountId}`);
              }
            })
            .then(res => { if (res) setAccountDetails(res.data); })
            .catch(err => console.error('Account by name error:', err));
        }
      }

    } catch (err) {
      setError('Error fetching case details: ' + (err.response?.data?.message || err.message));
    } finally {
      setLoading(false);
    }
  }, [caseId]);

  // Initial fetch
  useEffect(() => {
    fetchCaseData();
  }, [fetchCaseData]);

  // ============================================
  // REFRESH - force refetch everything
  // ============================================
  const refreshCaseData = useCallback(async () => {
    mainFetched.current = false;
    referenceFetched.current = false;
    remakeFetched.current = false;
    outsourceFetched.current = false;
    accountFetched.current = false;
    await fetchCaseData(true);
  }, [fetchCaseData]);

  // ============================================
  // UPDATE METHODS - optimistic updates + refetch
  // ============================================
  const updateCaseField = useCallback((field, value) => {
    setCaseData(prev => prev ? { ...prev, [field]: value } : prev);
  }, []);

  const updateMultipleFields = useCallback((fields) => {
    setCaseData(prev => prev ? { ...prev, ...fields } : prev);
  }, []);

  const value = {
    caseData,
    loading,
    error,
    referenceCaseData,
    remakeCases,
    outsourceCaseData,
    accountDetails,
    refreshCaseData,
    updateCaseField,
    updateMultipleFields,
    setCaseData,
    setAccountDetails,
    caseId,
  };

  return (
    <CaseDataContext.Provider value={value}>
      {children}
    </CaseDataContext.Provider>
  );
};

// ============================================
// Custom Hook
// ============================================
export const useCaseData = () => {
  const ctx = useContext(CaseDataContext);
  if (!ctx) {
    throw new Error('useCaseData must be used within CaseDataProvider');
  }
  return ctx;
};

export default CaseDataContext;